<script setup>
// The double-book warning paragraph, shared by the DoubleBookModal (session add)
// and the My Event conflict view (custom-event add/edit) so the four copy variants
// and the link behaviour live in one place. Copy comes from the pure
// doubleBookCopy() helper; this only handles rendering + the link.
import { doubleBookCopy, conflictKindFromId } from '~~/shared/utilities/schedule/double-book-copy';

const props = defineProps({
    newTitle: { type: String, default: '' },
    // 'session' when adding a catalog session, 'event' when adding a custom event.
    newKind: { type: String, default: 'session' },
    conflictTitle: { type: String, default: '' },
    // The conflicting item's unique_id. Drives both the session/event distinction
    // (via its `event:` prefix) and the detail-tray link.
    conflictId: { type: String, default: null },
    // Whether a CUSTOM-EVENT conflict should also be a link. Off by default: the
    // only destination for one is the My Event editor, and opening a different
    // event's editor from inside a half-filled My Event form would throw away
    // the user's input. The session-add modal opts in.
    linkEvents: { type: Boolean, default: false },
});

const conflictKind = computed(() => conflictKindFromId(props.conflictId));
const copy = computed(() => doubleBookCopy({
    newTitle: props.newTitle,
    newKind: props.newKind,
    conflictTitle: props.conflictTitle,
    conflictKind: conflictKind.value,
}));

// The point of this message is "go remove or reschedule the other thing", so the
// conflict name is the way there. A catalog session links to its detail tray.
// A custom event has no route — an earlier pass therefore rendered it as plain
// text, which QA bounced: the destination is the My Event EDITOR, not a tray, so
// the parent handles `navigate` itself and the anchor carries no href.
const linkable = computed(() => {
    if (!props.conflictId) return false;
    return conflictKind.value === 'session' || props.linkEvents;
});

const conflictHref = computed(() => (conflictKind.value === 'session' ? `/sessions/${props.conflictId}` : undefined));

// Close the surrounding modal first so the session tray opens over the page, not
// under the modal. The parent owns its own close, so it's told via `navigate`.
const emit = defineEmits(['navigate']);
</script>

<template>
    <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] text-(--magenta) m-0">
        {{ copy.before }}<a
            v-if="linkable"
            :href="conflictHref"
            :role="conflictHref ? undefined : 'button'"
            :tabindex="conflictHref ? undefined : 0"
            class="underline decoration-dotted decoration-(--dark-pink) underline-offset-2 cursor-pointer transition-colors duration-200 hover:no-underline hover:text-(--purple)"
            @click.prevent="emit('navigate', conflictId)"
            @keydown.enter.prevent="emit('navigate', conflictId)"
            @keydown.space.prevent="emit('navigate', conflictId)"
        >{{ copy.conflictName }}</a><span
            v-else
        >{{ copy.conflictName }}</span>{{ copy.after }}
    </p>
</template>
